import { LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Spin, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import dayjs from "utils/dayjs-config";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  DATE_FORMAT_MMDDYYYY_HHMM_A_Z,
  INPUT_DATE_FORMAT
} from "utils/dateutils";
import { useGetProjectINTGState } from "hooks/submittal";
import { getSubmittalIntegrationConfig } from "pages/subscription-settings/integrations-tab-new/utils";
import { invokeACCProjectSyncJob } from "services/integration-service";
import { ImportStatuses } from "utils/utils";
import { useParams } from "react-router";
import { isGcProjectAdminRole } from "../constants";

export function IntegrationProjectInfo(props: {
  syncBtnVisible: boolean;
  isFileBasedIntegration: boolean;
}) {
  const {
    tokenRetrievalState,
    tokenContents,
    gqlClientForProject,
    lastImportLogData,
    eventLogs,
    projectIntegrationConfig,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);

  const { syncBtnVisible, isFileBasedIntegration } = props;
  const { projectId } = useParams() as any;

  const isLoggedInUserGcAdmin = isGcProjectAdminRole(tokenContents?.role);

  const [invokingSync, setInvokingSync] = useState(false);
  const [projectInfoStr, setProjectInfoStr] = useState("");
  const [currentTime, setCurrentTime] = useState<any>(null);

  const { data: projectIntegrationData } = useGetProjectINTGState({
    client: gqlClientForProject
  });

  const timeUpdateIntervalRef = useRef<any>(null);

  const lastImportStatus: any = useMemo(() => {
    if (!lastImportLogData) return null;
    return lastImportLogData;
  }, [lastImportLogData]);

  // The raw project_integration_configs row this component speaks for. Not [0]:
  // that array is unordered and can now hold a schedule-only OPC config, which
  // has no parent subscription config for the account lookup below to walk, and
  // whose id must never reach the sync POST — see getSubmittalIntegrationConfig.
  const submittalProjectIntgConfig = useMemo(
    () =>
      getSubmittalIntegrationConfig(
        projectIntegrationData?.project_integration_configs
      ),
    [projectIntegrationData]
  );

  useEffect(() => {
    if (submittalProjectIntgConfig) {
      const projConfigProperties =
        submittalProjectIntgConfig.integration_config
          ?.integration_config_properties ?? [];
      const foundProjConfigProp = projConfigProperties.find(
        (projConfigProp: any) => {
          return (
            projConfigProp.property_name === "AUTODESK_PROJECT_NAME" ||
            projConfigProp.property_name === "PROCORE_PROJECT_NAME"
          );
        }
      );

      const foundSubConfigForThisProject =
        submittalProjectIntgConfig.system_integration_config?.subscription_integration_configs?.find(
          (subscriptionConfig: any) => {
            return (
              subscriptionConfig.integration_config.id ===
              submittalProjectIntgConfig.subscription_integration_config
                ?.config_id
            );
          }
        );

      const foundSubConfigProp =
        foundSubConfigForThisProject?.integration_config.integration_config_properties.find(
          (subConfigProp: any) => {
            return (
              subConfigProp.property_name === "AUTODESK_ACCOUNT_NAME" ||
              subConfigProp.property_name === "PROCORE_COMPANY_NAME"
            );
          }
        );

      if (syncBtnVisible) {
        setProjectInfoStr(
          `${foundSubConfigProp?.property_value ?? ""} - ${
            foundProjConfigProp?.property_value ?? ""
          }`
        );
      }
    }
  }, [submittalProjectIntgConfig, syncBtnVisible]);

  // ?? null, never undefined: .add(null) is a no-op +0, while .add(undefined) is
  // NaN and turns nextAllowedSync into an Invalid Date — which compares false
  // against every time, pinning the sync button off with a tooltip that reads
  // "You can only sync again after Invalid Date".
  const syncEnableInterval = useMemo(() => {
    return (
      getSubmittalIntegrationConfig(projectIntegrationConfig)
        ?.systemIntgConfigProperties?.SYNC_INTERVAL ?? null
    );
  }, [projectIntegrationConfig]);

  const canSyncSubmittals = useMemo(() => {
    const nextAllowedSubmittalSync = dayjs(
      lastImportLogData?.lastSyncDateTime,
      INPUT_DATE_FORMAT
    ).add(syncEnableInterval, "minutes");
    const allowSyncByTime = currentTime?.isSameOrAfter(
      nextAllowedSubmittalSync
    );
    return allowSyncByTime;
  }, [lastImportLogData?.lastSyncDateTime, syncEnableInterval, currentTime]);

  useEffect(() => {
    timeUpdateIntervalRef.current = setInterval(() => {
      setCurrentTime(dayjs());
      if (canSyncSubmittals) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    }, 1000);
    return () => clearInterval(timeUpdateIntervalRef.current);
  }, [canSyncSubmittals]);

  const syncButtonTooltip = useMemo(() => {
    if (isProjectArchived) return "";
    if (!currentTime || !lastImportStatus || !lastImportStatus.lastSyncDateTime)
      return "";

    if (lastImportStatus.status === ImportStatuses.PROCESSING) {
      return "Sync is in progress";
    }
    if (
      lastImportStatus.status === ImportStatuses.PROCESSED ||
      lastImportStatus.status === ImportStatuses.FAILED
    ) {
      // .tz() shifts to dayjs.tz.setDefault(projectZone). Without it the
      // Dayjs sits in machine-local, and the "(z)" format token below
      // renders "GMT±offset" instead of the project-zone abbreviation
      // (EDT, PDT…) on the sync-button hover tooltip.
      const nextAllowedSync = dayjs(
        lastImportStatus.lastSyncDateTime,
        INPUT_DATE_FORMAT
      )
        .tz()
        .add(syncEnableInterval, "minutes");
      const allowSyncByTime = currentTime.isSameOrAfter(nextAllowedSync);
      // An unformattable cooldown is not a cooldown worth reporting — "after
      // Invalid Date" gives the user nothing to wait for.
      if (!allowSyncByTime && nextAllowedSync.isValid())
        return (
          <div>
            <div>You can only sync again after</div>{" "}
            <div>{nextAllowedSync.format(DATE_FORMAT_MMDDYYYY_HHMM_A_Z)}</div>
          </div>
        );
    }
    return "Sync submittals";
  }, [isProjectArchived, lastImportStatus, currentTime, syncEnableInterval]);

  useEffect(() => {
    if (!lastImportStatus) return;
    const isSyncing = lastImportStatus.status === ImportStatuses.PROCESSING;
    setInvokingSync(isSyncing);
  }, [lastImportStatus]);

  const isSyncButtonReadOnly = useMemo(() => {
    if (isProjectArchived) return true;
    if (!currentTime || !lastImportStatus) return true;
    if (lastImportStatus.status === ImportStatuses.PROCESSING) return true;
    if (!lastImportStatus.lastSyncDateTime) return false;

    if (
      lastImportStatus.status === ImportStatuses.PROCESSED ||
      lastImportStatus.status === ImportStatuses.FAILED
    ) {
      const nextAllowedSync = dayjs(
        lastImportStatus.lastSyncDateTime,
        INPUT_DATE_FORMAT
      ).add(syncEnableInterval, "minutes");
      // Nothing to count from means no cooldown to enforce. Without this an
      // Invalid Date compares false against every time and locks the button off
      // permanently, with no tooltip left to explain why.
      if (!nextAllowedSync.isValid()) return false;
      const allowSyncByTime = currentTime.isSameOrAfter(nextAllowedSync);
      return !allowSyncByTime;
    }
    return false;
  }, [isProjectArchived, lastImportStatus, currentTime, syncEnableInterval]);

  const formattedLastSync = useMemo(() => {
    const lastSync = lastImportStatus?.lastSyncDateTime;
    if (!lastSync) return "NA";
    // Z-tagged UTC instant from backend → display in project zone (.tz() picks up
    // dayjs.tz.setDefault). Without this the user sees machine-local time, not
    // the project's time as the rest of the project clock indicates.
    return dayjs(lastSync, INPUT_DATE_FORMAT)
      .tz()
      .format(DATE_FORMAT_MMDDYYYY_HHMM_A);
  }, [lastImportStatus?.lastSyncDateTime]);

  const isProcessing = useMemo(
    () => lastImportStatus?.status === ImportStatuses.PROCESSING,
    [lastImportStatus?.status]
  );

  const isProcessedOrFailed = useMemo(
    () =>
      lastImportStatus?.status === ImportStatuses.PROCESSED ||
      lastImportStatus?.status === ImportStatuses.FAILED,
    [lastImportStatus?.status]
  );

  const importFileName = useMemo(
    () => lastImportLogData?.fileData?.file_name ?? "",
    [lastImportLogData?.fileData?.file_name]
  );

  const syncStatusMessage = useMemo(() => {
    const parts: string[] = [];
    let status = "";

    // FILE Based Integration
    if (isFileBasedIntegration) {
      if (importFileName) {
        parts.push(`(From: ${importFileName})`);
      }
      if (isProcessing) {
        status = "Import is in progress";
        parts.push(status);
      }

      if (isProcessedOrFailed) {
        status = `Last Import: ${formattedLastSync}`;
        parts.push(status);
      }

      return {
        tooltipMsg: parts.join(" | "),
        label: importFileName,
        importStatus: status
      };
    }

    // API Based Integration
    if (projectInfoStr !== "") {
      parts.push(`(From: ${projectInfoStr})`);
    }

    if (isProcessing) {
      status = "Sync is in progress";
      parts.push(status);
    }

    if (isProcessedOrFailed) {
      status = `Last synced: ${formattedLastSync}`;
      parts.push(status);
    }

    return {
      tooltipMsg: parts.join(" | "),
      label: projectInfoStr,
      importStatus: status
    };
  }, [
    isFileBasedIntegration,
    projectInfoStr,
    isProcessing,
    isProcessedOrFailed,
    formattedLastSync,
    importFileName
  ]);

  const syncData = async () => {
    // The sync button is `disabled` whenever isSyncButtonReadOnly is true, so its
    // onClick never fires in that state — this guard is unreachable from the UI.
    /* v8 ignore next */
    if (isSyncButtonReadOnly) return;
    // invoke-project-sync-job is system-agnostic: it runs whichever config it is
    // handed. Handing it [0] on a project that also carries an OPC config would
    // start an OPC schedule sync from the submittal log's sync button, so the row
    // is resolved by system instead. No submittal config, nothing to sync.
    const projConfigId: string | undefined = submittalProjectIntgConfig?.id;
    if (!projConfigId) return;
    setInvokingSync(true);
    const invokeSyncResponse = await invokeACCProjectSyncJob(
      {
        project_config_id: projConfigId
      },
      tokenRetrievalState.token
    );
    if (invokeSyncResponse.success) {
      message.info("Sync Started");
    }
    if (invokeSyncResponse?.error) {
      setInvokingSync(false);
    }
  };

  const importStatus = useCallback(
    (status: ImportStatuses) => {
      return eventLogs.some(
        (log) =>
          log.project_id === projectId &&
          log.change_type === "update" &&
          log.data_source === "import_log" &&
          (log.info.import_type === "ACC_IMPORT" ||
            log.info.import_type === "INTEGRATION_FILE_IMPORT") &&
          log.info.status === status
      );
    },
    [eventLogs, projectId]
  );

  useEffect(() => {
    if (importStatus(ImportStatuses.PROCESSED)) {
      message.success("Submittal Log imported successfully.");
    }

    if (importStatus(ImportStatuses.FAILED)) {
      message.error("Submittal Log failed to import.");
    }
  }, [importStatus]);

  if ((!projectInfoStr || !currentTime) && !isFileBasedIntegration) return null;

  return (
    <div
      className="flex items-center space-x-1 min-w-0 pr-2"
      title={syncStatusMessage.tooltipMsg}
    >
      <div className="flex items-center min-w-0 flex-1 text-black text-opacity-80 text-xs font-normal">
        {syncStatusMessage?.label !== "" && (
          <span className="mr-1 shrink-0">(From:</span>
        )}
        <Tooltip title={syncStatusMessage.label} placement="bottom">
          <span className="min-w-0 flex-1 truncate">
            {syncStatusMessage.label}
          </span>
        </Tooltip>
        {syncStatusMessage?.label !== "" && <span className="shrink-0">)</span>}
        {syncStatusMessage.importStatus && (
          <span className="ml-1 shrink-0">
            | {syncStatusMessage.importStatus}
          </span>
        )}
      </div>
      {isLoggedInUserGcAdmin && syncBtnVisible && (
        <div className="shrink-0">
          <Tooltip
            title={syncButtonTooltip}
            destroyOnHidden
            placement="right"
            popupVisible={!!syncButtonTooltip}
          >
            <Button
              className="-mt-1.5"
              disabled={invokingSync || isSyncButtonReadOnly}
              size="small"
              icon={
                invokingSync ? (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{ color: "var(--ciq-fg)" }}
                        spin
                      />
                    }
                    size="small"
                  />
                ) : (
                  <SyncOutlined spin={invokingSync} />
                )
              }
              onClick={syncData}
              data-testid="apiBased-sync-btn"
            />
          </Tooltip>
        </div>
      )}

      {isFileBasedIntegration && lastImportStatus?.status === "PROCESSING" && (
        <div className="shrink-0">
          <Tooltip placement="right">
            <Button
              className="cursor-default -mt-1"
              size="small"
              icon={
                <Spin
                  indicator={
                    <LoadingOutlined style={{ color: "var(--ciq-fg)" }} spin />
                  }
                  size="small"
                />
              }
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
